import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, Printer } from 'lucide-react';
import { getSettings, openPalletLabels } from '../lib/api';

/**
 * AldiBranchDialog — shown before printing an Aldi pallet label.
 * Lets the user pick which Aldi branch (Mitchelstown, Naas, etc.)
 * so only the To address changes; everything else stays the same.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   dispatchId — string
 *
 * SEC-001: no session JWT is embedded in the URL anymore — the label URL is
 * opened through `openPalletLabels`, which mints a single-use download token
 * server-side and appends only that short-lived token to the query string.
 */
export default function AldiBranchDialog({ open, onClose, dispatchId }) {
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getSettings()
      .then(res => {
        const b = res.data?.aldi_branches || [];
        setBranches(b);
        if (b.length > 0) setSelectedBranch(b[0].id);
      })
      .catch(() => setBranches([]))
      .finally(() => setLoading(false));
  }, [open]);

  const handlePrint = async () => {
    const extra = { template: 'aldi' };
    if (selectedBranch) extra.branch = selectedBranch;
    await openPalletLabels(dispatchId, 2, extra);
    onClose();
  };

  const selectedName = branches.find(b => b.id === selectedBranch)?.name || '';

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="aldi-branch-dialog">
        <DialogHeader>
          <DialogTitle>Aldi Delivery Docket</DialogTitle>
          <DialogDescription>Select the Aldi branch for the delivery address</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
        ) : branches.length === 0 ? (
          <div className="py-4 text-sm text-zinc-500 text-center">
            No branches configured. Go to <strong>Template Designer → Aldi</strong> to add branches, or print with the default template address.
          </div>
        ) : (
          <div className="py-3 space-y-3">
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger data-testid="branch-selector">
                <SelectValue placeholder="Select branch..." />
              </SelectTrigger>
              <SelectContent>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id} data-testid={`branch-${b.id}`}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedBranch && (
              <div className="rounded border bg-zinc-50 p-3 text-sm whitespace-pre-line" data-testid="branch-preview">
                <div className="text-xs font-semibold text-zinc-400 mb-1">To Address Preview:</div>
                {branches.find(b => b.id === selectedBranch)?.to_address || 'No address set'}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handlePrint} data-testid="btn-print-aldi">
            <Printer className="w-4 h-4 mr-2" />
            {selectedName ? `Print — ${selectedName}` : 'Print Aldi Label'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
